import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Briefcase, Calendar, MapPin, CurrencyEur, GraduationCap, ListChecks } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { portalListMissions } from '../services/api';
import { formatApiError } from '../utils/apiError';

const TYPE_LABELS = {
  mission_coop_cipaesse: 'Mission C.I.P.A.E.S.S.E (O\u2019SCOP)',
  mission_coop_cipascop: 'Mission C.I.P.A.S.C.O.P',
};

const STATUS_COLORS = {
  brouillon: 'bg-slate-100 text-slate-700',
  en_cours: 'bg-blue-100 text-blue-700',
  valide: 'bg-green-100 text-green-700',
  cloture: 'bg-gray-100 text-gray-700',
};

const fmt = (v) => {
  if (v == null || v === '') return '\u2014';
  return v;
};

const fmtDate = (iso) => {
  if (!iso) return '\u2014';
  try { return new Date(iso).toLocaleDateString('fr-FR'); } catch { return iso; }
};

const fmtEur = (v) => {
  if (v == null || v === '') return '\u2014';
  const n = Number(v);
  if (isNaN(n)) return '\u2014';
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' \u20AC';
};

export function PortalMissionsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cooperator, setCooperator] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await portalListMissions();
        setItems(res.data.items || []);
        setCooperator(res.data.cooperator || null);
      } catch (e) {
        toast.error(formatApiError(e));
      }
      setLoading(false);
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-[#2D5A4C]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="portal-missions-page">
      <div>
        <h1 className="text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>Mes missions coopératives</h1>
        <p className="text-sm text-[#5E6D66] mt-1">
          {cooperator ? `Bienvenue ${cooperator.first_name || cooperator.legal_name || ''}, ` : ''}
          voici les missions coopératives O’SCOP dans lesquelles vous êtes engagé(e).
        </p>
      </div>

      {items.length === 0 ? (
        <Card className="border-[#E8E6E1]">
          <CardContent className="py-12 text-center">
            <Briefcase size={48} className="mx-auto text-[#8DB0A1] mb-3" weight="thin" />
            <p className="text-[#5E6D66] text-sm">Aucune mission coopérative pour le moment.</p>
            <p className="text-xs text-[#8DB0A1] mt-1">Vos futures missions apparaîtront ici dès qu’elles seront créées.</p>
          </CardContent>
        </Card>
      ) : (
        items.map((m) => (
          <Card key={m.id} className="border-[#E8E6E1] hover:shadow-md transition-shadow" data-testid={`portal-mission-${m.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Briefcase size={16} className="text-[#2D5A4C]" />
                    <Badge variant="outline" className="text-[10px]">{TYPE_LABELS[m.type_dossier] || m.type_dossier}</Badge>
                    <span className="text-[10px] font-mono text-[#5E6D66]">{m.numero_dossier}</span>
                  </div>
                  <h3 className="text-base font-semibold text-[#1A2421]">{fmt(m.mission_intitule) || m.objet_besoin || 'Mission sans titre'}</h3>
                </div>
                <Badge className={`text-xs ${STATUS_COLORS[m.statut] || 'bg-slate-100 text-slate-700'}`}>{fmt(m.statut)}</Badge>
              </div>

              {m.mission_descriptif && (
                <p className="text-sm text-[#3D4E48] mb-3">{m.mission_descriptif}</p>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div className="flex items-start gap-2">
                  <MapPin size={12} className="text-[#5E6D66] mt-0.5" />
                  <div>
                    <p className="text-[#5E6D66]">Lieu</p>
                    <p className="font-medium text-[#1A2421]">{fmt(m.mission_lieu)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Calendar size={12} className="text-[#5E6D66] mt-0.5" />
                  <div>
                    <p className="text-[#5E6D66]">Période</p>
                    <p className="font-medium text-[#1A2421]">{fmtDate(m.mission_date_debut)} → {fmtDate(m.mission_date_fin)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <GraduationCap size={12} className="text-[#5E6D66] mt-0.5" />
                  <div>
                    <p className="text-[#5E6D66]">Qualification</p>
                    <p className="font-medium text-[#1A2421]">{fmt(m.mission_qualification)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <CurrencyEur size={12} className="text-[#5E6D66] mt-0.5" />
                  <div>
                    <p className="text-[#5E6D66]">Paiement prévisionnel</p>
                    <p className="font-medium text-[#1A2421]">{fmtEur(m.mission_paiement_previsionnel)}</p>
                  </div>
                </div>
              </div>

              {m.mission_competences && (
                <div className="mt-3 pt-3 border-t border-[#F0EFEB]">
                  <div className="flex items-start gap-2">
                    <ListChecks size={12} className="text-[#5E6D66] mt-0.5" />
                    <div>
                      <p className="text-xs text-[#5E6D66]">Compétences requises</p>
                      <p className="text-xs font-medium text-[#1A2421]">{m.mission_competences}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
